import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ConversionPreview } from "@/components/conversion-preview/ConversionPreview";
import { createShareFormSchema, ShareFormValues } from "./schemas/shareForm.schema";
import { useCreateSaccoShareWithDebit, useUpdateSaccoShare } from "@saccoHooks";
import { useFetchAccounts } from "@accountHooks";
import { useCurrencyList } from "@/hooks/useCurrencyList";
import { useExchangeRates } from "@/hooks/useExchangeRates";
import { getDisplayName, disambiguateDisplayNames } from "@/lib/display-name";
import {SaccoShare} from "@/@types/sacco.models.ts";

interface ShareFormProps {
  membershipId: string;
  nativeCurrency?: string;
  share?: SaccoShare;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function ShareForm({
  membershipId,
  nativeCurrency = "KES",
  share,
  open,
  onOpenChange,
  onSuccess,
}: ShareFormProps) {
  const isEdit = !!share;
  const schema = createShareFormSchema(isEdit);

  const createShare = useCreateSaccoShareWithDebit();
  const updateShare = useUpdateSaccoShare(membershipId);

  const { data: accounts, isLoading: isLoadingAccounts } = useFetchAccounts();
  const { data: currencyList, isLoading: isLoadingCurrencies } = useCurrencyList();
  const { getRate, isLoading: isLoadingRates } = useExchangeRates();

  const currencyManuallyChanged = useRef(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");

  const form = useForm<ShareFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      units: "" as unknown as number,
      price_per_unit: "" as unknown as number,
      purchase_date: "",
      note: "",
      source_account_id: "",
      fee: undefined,
      share_currency: "",
    },
  });

  useEffect(() => {
    if (open) {
      currencyManuallyChanged.current = false;
      form.reset({
        units: share?.units ? Number(share.units) : ("" as unknown as number),
        price_per_unit: share?.price_per_unit
          ? Number(share.price_per_unit)
          : ("" as unknown as number),
        purchase_date: share?.purchase_date ?? "",
        note: share?.note ?? "",
        source_account_id: "",
        fee: undefined,
        share_currency: "",
      });
      setSelectedAccountId("");
    }
  }, [open, share, form]);

  const isPending = createShare.isPending || updateShare.isPending;

  const selectedAccount = accounts?.find((a) => a.id === selectedAccountId);
  const sourceAccountNativeCurrency = selectedAccount?.currency ?? "";
  const shareCurrency = form.watch("share_currency") ?? "";
  const unitsValue = form.watch("units");
  const priceValue = form.watch("price_per_unit");

  // Auto-default share_currency to the source account's native currency
  useEffect(() => {
    if (!currencyManuallyChanged.current && sourceAccountNativeCurrency) {
      form.setValue("share_currency", sourceAccountNativeCurrency);
    }
  }, [sourceAccountNativeCurrency, form]);

  const parsedUnits = Number(unitsValue) || 0;
  const parsedPrice = Number(priceValue) || 0;
  const totalAmount = parsedUnits * parsedPrice;

  const showConversionPreview =
    !isEdit &&
    !!shareCurrency &&
    !!sourceAccountNativeCurrency &&
    shareCurrency !== sourceAccountNativeCurrency &&
    totalAmount > 0;

  const disambiguatedAccounts = disambiguateDisplayNames(
    (accounts ?? []).map((a) => ({ ...a, displayName: getDisplayName(a) }))
  );

  async function onSubmit(values: ShareFormValues) {
    try {
      if (isEdit) {
        await updateShare.mutateAsync({
          id: share.id,
          units: values.units,
          pricePerUnit: values.price_per_unit,
          purchaseDate: values.purchase_date,
          note: values.note || null,
        });
        toast.success("Share record updated successfully");
      } else {
        await createShare.mutateAsync({
          membershipId,
          sourceAccountId: values.source_account_id!,
          units: values.units,
          pricePerUnit: values.price_per_unit,
          shareCurrency: values.share_currency || nativeCurrency,
          fee: values.fee ?? 0,
          purchaseDate: values.purchase_date,
          note: values.note || null,
        });
        toast.success("Share purchase recorded successfully");
      }
      onOpenChange(false);
      onSuccess?.();
    } catch {
      toast.error(
        isEdit ? "Failed to update share record" : "Failed to record share purchase"
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Share Purchase" : "Add Share Purchase"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Create-only: Source account */}
            {!isEdit && (
              <FormField
                control={form.control}
                name="source_account_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Source Account *</FormLabel>
                    <FormControl>
                      <Select
                        disabled={isLoadingAccounts}
                        value={field.value ?? ""}
                        onValueChange={(val) => {
                          field.onChange(val);
                          setSelectedAccountId(val);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              isLoadingAccounts
                                ? "Loading accounts…"
                                : "Select source account"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {disambiguatedAccounts.map((account) => (
                            <SelectItem key={account.id} value={account.id}>
                              {account.displayName} ({account.currency})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    {selectedAccount && (
                      <p className="text-sm text-muted-foreground">
                        {selectedAccount.balance.toFixed(2)}{" "}
                        {selectedAccount.currency}
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Units & Price */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="units"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Units *</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="0"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="price_per_unit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {`Price per Unit (${shareCurrency || nativeCurrency}) *`}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="0.00"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Create-only: Share currency */}
            {!isEdit && (
              <FormField
                control={form.control}
                name="share_currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Share Currency *</FormLabel>
                    <FormControl>
                      <Select
                        disabled={isLoadingCurrencies}
                        value={field.value ?? ""}
                        onValueChange={(val) => {
                          currencyManuallyChanged.current = true;
                          field.onChange(val);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              isLoadingCurrencies
                                ? "Loading currencies…"
                                : "Select currency"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {(currencyList ?? []).map((c) => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Create-only: Fee */}
            {!isEdit && (
              <FormField
                control={form.control}
                name="fee"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Fee{" "}
                      <span className="text-muted-foreground font-normal">
                        (optional)
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === ""
                              ? undefined
                              : Number(e.target.value)
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* ConversionPreview */}
            {showConversionPreview && (
              <ConversionPreview
                amount={totalAmount}
                fromCurrency={shareCurrency}
                toCurrency={sourceAccountNativeCurrency}
                label="Estimated debit"
                getRate={getRate}
                isLoadingRates={isLoadingRates}
              />
            )}

            <FormField
              control={form.control}
              name="purchase_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Purchase Date *</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Optional note"
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Saving..." : isEdit ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
